// account-email-password.js — Change-email + Change-password modals
// for the Account page.
//
// Email-change flow: Supabase sends confirmation links to BOTH the
// current and the new inbox. The change takes effect when the user
// clicks the link in the NEW inbox. We swap the modal body to a
// "sent" state with a Resend button rather than closing it — users
// frequently miss the confirmation email and re-finding the dialog
// is friction.
//
// Password-change flow: validate, acquire a reauth (sudo-mode)
// token, POST /v1/user/password with that token, then force-sign-
// out everywhere (Supabase invalidates other sessions on password
// change so we mirror that locally). The sign-out tail uses
// bootstrap-owned auth state (booted, state.token, showAuth) —
// wrapped in a single AC_LEGACY.signOutAfterPasswordChange bridge
// entry to keep this module decoupled.
//
// Wired entirely at module init — no exports. The Open buttons
// (#account-change-email + #account-change-password) live in the
// static HTML scaffold so module-init wiring matches legacy
// behavior.
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K Tier 2d); reauth
// bridge calls switched to direct imports in Tier 2n.
import { $ } from "./dom.js";
import { showToast } from "./ui.js";
import { requireReauth, clearReauth } from "./reauth.js";

const COORDINATOR =
  (typeof window !== "undefined" &&
    window.AC_CONFIG &&
    window.AC_CONFIG.API_BASE) ||
  "";

function getSupabase() {
  const legacy = window.AC_LEGACY;
  return legacy && typeof legacy.getSupabase === "function"
    ? legacy.getSupabase()
    : null;
}

function bridgeCall(name, ...args) {
  const legacy = window.AC_LEGACY;
  const fn = legacy && legacy[name];
  if (typeof fn === "function") return fn(...args);
  return undefined;
}

// ---------- email modal ----------

function openEmailModal() {
  const newEl = $("email-new");
  const errEl = $("email-error");
  const bd = $("email-change-backdrop");
  if (newEl) newEl.value = "";
  if (errEl) errEl.textContent = "";
  if (bd) bd.classList.add("open");
  setTimeout(() => {
    if (newEl) newEl.focus();
  }, 50);
}

function closeEmailModal() {
  const bd = $("email-change-backdrop");
  if (bd) bd.classList.remove("open");
}

// showEmailSentState swaps the email-change modal body to a "sent"
// confirmation with a Resend button. Resend just calls updateUser
// again with the same address — Supabase treats this as a re-send
// of the same pending change, no error. Per Supabase docs, both
// inboxes (current + new) get a fresh email; clicking the link in
// the NEW one finalizes the change.
function showEmailSentState(newEmail) {
  const backdrop = $("email-change-backdrop");
  const box = backdrop && backdrop.querySelector(".confirm-box");
  if (!box) return;
  // Inline escape: keep this module dependency-light (one helper),
  // matching the original.
  const escaped = newEmail.replace(
    /[<>&"]/g,
    (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" })[c],
  );
  box.innerHTML =
    "" +
    '<div class="confirm-title">Confirmation sent</div>' +
    '<div class="confirm-body" style="margin-bottom: var(--s4); font-size: 13px; color: var(--text-secondary); line-height: 1.6;">' +
    "We sent a link to <strong>both</strong> your current and new inbox. Supabase requires you to click the link in your <strong>new</strong> inbox (" +
    escaped +
    ") to finish the change. Until then, your account stays at the old address." +
    "<br><br>" +
    "Didn’t get the email? Check spam, or resend below." +
    "</div>" +
    '<div id="email-resend-status" style="font-size: 12px; color: var(--text-tertiary); min-height: 16px; margin-bottom: var(--s3);"></div>' +
    '<div class="confirm-actions">' +
    '<button class="btn-cancel" id="email-resend">Resend</button>' +
    '<button class="btn-confirm-primary" id="email-done">Done</button>' +
    "</div>";
  const statusEl = box.querySelector("#email-resend-status");
  box
    .querySelector("#email-done")
    .addEventListener("click", closeEmailModal);
  box.querySelector("#email-resend").addEventListener("click", async (e) => {
    const btn = e.currentTarget;
    const prev = btn.textContent;
    btn.disabled = true;
    btn.textContent = "Resending…";
    statusEl.textContent = "";
    statusEl.style.color = "var(--text-tertiary)";
    try {
      const supabase = getSupabase();
      if (!supabase) throw new Error("Auth not ready.");
      const { error } = await supabase.auth.updateUser({ email: newEmail });
      if (error) throw error;
      statusEl.textContent = "Resent. Check both inboxes again.";
    } catch (err) {
      statusEl.style.color = "var(--danger)";
      statusEl.textContent = (err && err.message) || "Resend failed.";
    } finally {
      btn.disabled = false;
      btn.textContent = prev;
    }
  });
}

// ---------- password modal ----------

function openPasswordModal() {
  const pwNew = $("password-new");
  const pwConfirm = $("password-confirm");
  const errEl = $("password-error");
  const bd = $("password-change-backdrop");
  if (pwNew) pwNew.value = "";
  if (pwConfirm) pwConfirm.value = "";
  if (errEl) errEl.textContent = "";
  if (bd) bd.classList.add("open");
  setTimeout(() => {
    if (pwNew) pwNew.focus();
  }, 50);
}

function closePasswordModal() {
  const bd = $("password-change-backdrop");
  if (bd) bd.classList.remove("open");
}

// ---- module init: wire both modals' open/cancel/submit handlers ----

function wireListeners() {
  // ----- email modal -----
  const openEmailBtn = $("account-change-email");
  if (openEmailBtn) openEmailBtn.addEventListener("click", openEmailModal);
  const emailCancel = $("email-cancel");
  if (emailCancel) emailCancel.addEventListener("click", closeEmailModal);
  const emailBackdrop = $("email-change-backdrop");
  if (emailBackdrop) {
    emailBackdrop.addEventListener("click", (e) => {
      if (e.target === emailBackdrop) closeEmailModal();
    });
  }
  const emailSubmit = $("email-submit");
  if (emailSubmit) {
    emailSubmit.addEventListener("click", async () => {
      const newEmail = ($("email-new").value || "").trim();
      const errEl = $("email-error");
      errEl.textContent = "";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
        errEl.textContent = "Enter a valid email address.";
        return;
      }
      const btn = emailSubmit;
      const prev = btn.textContent;
      btn.disabled = true;
      btn.textContent = "Sending…";
      try {
        const supabase = getSupabase();
        if (!supabase) throw new Error("Auth not ready.");
        const { error } = await supabase.auth.updateUser({ email: newEmail });
        if (error) throw error;
        // Don't close — swap to a "sent" state with a Resend button.
        showEmailSentState(newEmail);
      } catch (e) {
        errEl.textContent = (e && e.message) || "Could not send confirmation.";
      } finally {
        btn.disabled = false;
        btn.textContent = prev;
      }
    });
  }

  // ----- password modal -----
  const openPwBtn = $("account-change-password");
  if (openPwBtn) openPwBtn.addEventListener("click", openPasswordModal);
  const pwCancel = $("password-cancel");
  if (pwCancel) pwCancel.addEventListener("click", closePasswordModal);
  const pwBackdrop = $("password-change-backdrop");
  if (pwBackdrop) {
    pwBackdrop.addEventListener("click", (e) => {
      if (e.target === pwBackdrop) closePasswordModal();
    });
  }
  const pwSubmit = $("password-submit");
  if (pwSubmit) {
    pwSubmit.addEventListener("click", async () => {
      const supabase = getSupabase();
      if (!supabase) return;
      const btn = pwSubmit;
      const cancelBtn = $("password-cancel");
      const pw = $("password-new").value;
      const confirm = $("password-confirm").value;
      const err = $("password-error");
      err.textContent = "";
      if (pw.length < 8) {
        err.textContent = "Password must be at least 8 characters.";
        return;
      }
      if (pw !== confirm) {
        err.textContent = "Passwords don’t match.";
        return;
      }

      const prevText = btn.textContent;
      btn.disabled = true;
      if (cancelBtn) cancelBtn.disabled = true;
      btn.textContent = "Updating…";

      // Acquire sudo-mode reauth token. Cancel → silent abort.
      let reauthToken;
      try {
        reauthToken = await requireReauth();
      } catch (e) {
        btn.disabled = false;
        if (cancelBtn) cancelBtn.disabled = false;
        btn.textContent = prevText;
        if (e && e.message === "reauth_cancelled") return;
        err.textContent = e.message || "Could not verify password.";
        return;
      }

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) throw new Error("Not signed in.");
        const r = await fetch(COORDINATOR + "/v1/user/password", {
          method: "POST",
          headers: {
            Authorization: "Bearer " + session.access_token,
            "X-Reauth-Token": reauthToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ new_password: pw }),
        });
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          // 403 reauth_required = our cached token was bad; drop it so
          // the next attempt re-prompts instead of looping with stale
          // state.
          if (r.status === 403 || e.error === "reauth_required") {
            clearReauth();
          }
          throw new Error(
            e.message || e.error || "Could not update password.",
          );
        }
        clearReauth(); // server consumed the token
        closePasswordModal();
        showToast(
          "success",
          "Password updated. All devices have been signed out. Please sign in again.",
          { duration: 6000 },
        );
        // Wait for toast to render, then sign out + bounce to auth.
        await new Promise((res) => setTimeout(res, 1500));
        await bridgeCall("signOutAfterPasswordChange");
      } catch (e) {
        err.textContent = e.message || "Could not update password.";
      } finally {
        btn.disabled = false;
        if (cancelBtn) cancelBtn.disabled = false;
        btn.textContent = prevText;
      }
    });
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireListeners);
} else {
  wireListeners();
}
